import type { ReportType } from '../../models/end-client-reporting-package';

/**
 * What the module guide says about each module, beyond the one line the picker shows: when to reach
 * for it, what it needs from the survey, where it misleads, and how to get the most from it.
 *
 * Written for a researcher choosing between modules, not for a developer. Exhaustive, so a new
 * module cannot ship without its entry.
 */
export interface ModuleGuide {
  /** The situations it is the right answer for. */
  bestFor: string[];
  /** The kind of question it reads, in survey terms. */
  needs: string;
  /** Where it misleads, or a better module to use instead. */
  watchOut: string;
  /** Settings worth knowing about. */
  tips: string[];
  /** Neighbours a reader might be choosing between. */
  related: ReportType[];
}

export const MODULE_GUIDE: Record<ReportType, ModuleGuide> = {
  'study-overview': {
    bestFor: ['The opening page of any deck', 'Setting expectations on sample size before a client reads a percentage'],
    needs: 'Nothing to choose: sample size and interview length come from the data, incidence from the fieldwork counts.',
    watchOut: 'Market and notes are typed, so check them whenever the deck is reused for another wave.',
    tips: ['Any figure can be overridden by typing over it -- useful for a blended or quota-adjusted sample.', 'Notes take *bold* and "- " bullets.'],
    related: ['headline-stats'],
  },
  'headline-stats': {
    bestFor: ['An executive summary page', 'The three or four numbers someone should remember'],
    needs: 'Nothing from the data -- every figure and line is typed, because these are conclusions.',
    watchOut: 'Typed figures do not update when the data refreshes. Use a KPI Score for a number that should.',
    tips: ['Keep labels to a few words; the figure carries the message.', 'Takeaways read best as full sentences with the "so what" in them.'],
    related: ['kpi-score', 'action-list'],
  },
  'text': {
    bestFor: ['Introducing a deck or a section', 'Describing what the charts on a page show', 'Notes on method or definitions'],
    needs: 'Nothing from the data -- the text is written, by hand or by asking the AI chat.',
    watchOut: 'Figures typed into text do not update when the data refreshes. Point at the chart beside it instead of copying its number.',
    tips: ['Headings, bold, italics, lists and links are all available from the toolbar.', 'Ask the AI chat to "add a description of this page" for a first draft.'],
    related: ['headline-stats', 'image'],
  },
  'image': {
    bestFor: ['Showing the product, pack or creative a page is about', 'A diagram that explains a method'],
    needs: 'Nothing from the data -- upload a JPEG, PNG, GIF or WebP picture.',
    watchOut: 'For where people clicked on a piece of creative, use a Heatmap: it shades the survey\'s own image by the answers.',
    tips: ['Fill in the description for anyone who can\'t see the picture.', 'Half width suits a picture beside the chart it illustrates.'],
    related: ['heatmap', 'text'],
  },
  'action-list': {
    bestFor: ['Closing a deck with recommendations', 'Agreeing priorities in a debrief'],
    needs: 'Nothing from the data -- the actions are written.',
    watchOut: 'Each action should trace back to a finding earlier in the deck; one that does not reads as opinion.',
    tips: ['Use the priority on each to order the conversation, not just the list.'],
    related: ['headline-stats'],
  },
  'kpi-score': {
    bestFor: ['A single headline metric -- NPS, net satisfaction, consideration', 'Tracking one number across waves'],
    needs: 'A rating scale (single answer) whose answers can be grouped into bands, or nothing at all for a typed figure.',
    watchOut: 'Check which end of the scale is favourable: a scale asked worst-first nets backwards unless "favourable end" is set.',
    tips: ['Bands make the score: NPS is Promoters (9-10) minus Detractors (0-6).', '"Share" shows one band\'s size instead of a net.', 'Show it as a dial or as the figure alone.'],
    related: ['stacked-scale', 'segment-tiles'],
  },
  'segment-tiles': {
    bestFor: ['One measure across markets, regions or customer types', 'Spotting the outlier segment at a glance'],
    needs: 'The measure (one question) and a question that splits people into groups.',
    watchOut: 'Small segments make big swings. Check each tile\'s base before reading a gap as real.',
    tips: ['"Answers counted" decides the figure -- tick the top two boxes for a satisfaction score.', 'Sort the tiles to rank the segments.'],
    related: ['subgroup-panels', 'crosstab'],
  },
  'bar-chart': {
    bestFor: ['The distribution of one question', 'Ranking the options of a multi-choice question', 'Averages across a numeric battery'],
    needs: 'Any closed question, several questions side by side, or a numeric battery such as MaxDiff scores.',
    watchOut: 'On a multi-choice question bars total more than 100% -- say so in the title or subtitle.',
    tips: ['Sort and "Show top" turn a long list into a ranking.', 'Bands colour an ordered scale; categories group rows by theme.'],
    related: ['pie-chart', 'stacked-scale', 'filter-comparison'],
  },
  'pie-chart': {
    bestFor: ['Shares of a whole with a handful of answers', 'A simple "how split is the market" view'],
    needs: 'One single-answer question, ideally six answers or fewer.',
    watchOut: 'Hard to compare slices of similar size; past six answers a bar chart reads better.',
    tips: ['A multi-answer question draws as a small donut per option instead, since its answers overlap.'],
    related: ['donut-chart', 'bar-chart'],
  },
  'donut-chart': {
    bestFor: ['The same jobs as a pie, with a lighter look', 'Pairing with a headline figure'],
    needs: 'One single-answer question, ideally six answers or fewer.',
    watchOut: 'As with a pie, similar slices are hard to tell apart.',
    tips: ['On a multi-answer question each option becomes its own small donut.'],
    related: ['pie-chart', 'bar-chart'],
  },
  'stacked-scale': {
    bestFor: ['An agreement or satisfaction battery', 'Comparing several statements on one scale', 'Net scores down the side'],
    needs: 'One or more questions sharing the same ordered answer list.',
    watchOut: 'Statements on different scales cannot share the bar; split them into separate modules.',
    tips: ['Bands name the ends ("Agree", "Disagree") and drive the net column.', 'Order bars by net to put the strongest statement first.', '"Not sure" can be excluded from the scale.'],
    related: ['diverging-bars', 'kpi-score', 'stage-matrix'],
  },
  'filter-comparison': {
    bestFor: ['Showing how a filtered group differs from everyone', 'Interactive client decks where readers change the filters'],
    needs: 'One closed question, and filters on the deck.',
    watchOut: 'The two bars are identical until a filter is chosen, which can look broken in a static export.',
    tips: ['The grey bar is always everyone; the coloured bar follows the filters.'],
    related: ['subgroup-panels', 'crosstab'],
  },
  'subgroup-panels': {
    bestFor: ['The same question shown for each subgroup', 'Seeing shape differences rather than exact figures'],
    needs: 'One question, and a question (or a set of answers) to split by.',
    watchOut: 'Each panel is a smaller sample; small panels exaggerate differences.',
    tips: ['Keep the Total panel -- every other panel is read against it.', 'Columns can mix questions: "Male", "Female", "18-34".'],
    related: ['crosstab', 'segment-tiles'],
  },
  'crosstab': {
    bestFor: ['Banner tables: one question against several subgroups at once', 'When exact figures and significance matter', 'Reports made entirely of tables'],
    needs: 'A question for the rows and subgroups for the columns -- answers of one or several questions.',
    watchOut: 'Columns from different questions overlap ("Male" and "18-34" share people), so only columns of the same question are tested against each other.',
    tips: [
      'Significance letters mark a cell higher than that column; arrows compare with everyone else.',
      'Nets combine answers (Top 2 box); a mean row scores a scale by its codes.',
      '"Split by" adds a third question as nested columns, tabs or a grid.',
      'Readers can click any column, row or cell to drill into those respondents.',
      '"Make tables for more questions" copies the setup across the survey.',
    ],
    related: ['subgroup-panels', 'segment-tiles', 'filter-comparison'],
  },
  'diverging-bars': {
    bestFor: ['Two-sided trade-offs ("speed or quality")', 'A battery of either/or choices'],
    needs: 'One question per row, each with two poles and optionally a middle answer.',
    watchOut: 'Check which pole is drawn on the left -- the datamap does not say which is which.',
    tips: ['The gap column shows which side wins and by how much.', 'Categories group rows into named themes.'],
    related: ['stacked-scale', 'bar-chart'],
  },
  'tiered-ranking': {
    bestFor: ['Ranking questions -- what matters most', 'Turning priorities into named tiers'],
    needs: 'A ranking grid: one question per item, each answered with a rank position.',
    watchOut: 'A ranking says what wins, not by how much; pair with an importance rating for strength.',
    tips: ['"Places counted as top" sets the top-N share each bar shows.', 'Name the tiers ("Must-haves", "Differentiators").'],
    related: ['bar-chart', 'ownership-matrix'],
  },
  'funnel': {
    bestFor: ['Awareness to purchase journeys', 'Showing where people drop out'],
    needs: 'One question per stage, where each stage genuinely contains the next.',
    watchOut: 'If a later stage is not a subset of the one before, the shape lies -- use a bar chart instead.',
    tips: ['"Answers counted" sets which answer means "reached this stage".', 'The drop-off is a share of the stage above, not percentage points.'],
    related: ['bar-chart', 'stage-matrix'],
  },
  'ownership-matrix': {
    bestFor: ['Brand-attribute association grids', 'Finding the attributes nobody owns yet'],
    needs: 'One multi-choice question per attribute, each answered with the same list of brands.',
    watchOut: 'Bigger brands get more mentions on everything; read a brand\'s pattern, not just its totals.',
    tips: ['Add a ranking grid to get a priority column and sort rows by what matters.', '"White space" shows the share who associate the attribute with nobody.'],
    related: ['tiered-ranking', 'heatmap'],
  },
  'heatmap': {
    bestFor: ['Ad, pack and menu testing', 'Seeing where on an image attention lands'],
    needs: 'An image-highlighter question, and the image respondents marked.',
    watchOut: 'The image must be the exact file the survey showed -- the marks are placed by its pixels.',
    tips: ['Use one colour scale across categories to compare "like" with "dislike".', 'The "none of these" share sits under the image.'],
    related: ['open-end-explorer', 'ownership-matrix'],
  },
  'stage-matrix': {
    bestFor: ['Brand relationship ladders', 'Any battery on one ordered scale read as stages'],
    needs: 'One single-answer question per row, all sharing the same answer list.',
    watchOut: 'Stage names are written by you; they need checking whenever the scale changes.',
    tips: ['Group the columns into named stages and add a total column (e.g. aided awareness).', 'Turn on an explainer to teach readers the ladder.'],
    related: ['stage-explainer', 'stacked-scale'],
  },
  'stage-explainer': {
    bestFor: ['Teaching a reader what each stage of a ladder means'],
    needs: 'A Stage Matrix -- the explainer is switched on from the matrix\'s own settings, not added on its own.',
    watchOut: 'Its stage names come from the matrix, so rename them there.',
    tips: ['Keep each point to a short phrase; it sits beside or before the matrix.'],
    related: ['stage-matrix'],
  },
  'scatter-plot': {
    bestFor: ['Importance against performance', 'Positioning maps of items on two measures'],
    needs: 'Exactly two questions measuring the same items -- two numeric batteries, or two grids.',
    watchOut: 'Average lines split the quadrants; with few items the averages move a lot.',
    tips: ['Name the top-right quadrant for the story you are telling.', 'Axis titles can be shorter than the questions.'],
    related: ['ownership-matrix', 'bar-chart'],
  },
  'open-end-explorer': {
    bestFor: ['Reading what people actually said', 'Quotes for a debrief'],
    needs: 'One open-ended question.',
    watchOut: 'Answers are shown as given -- check for anything identifying before sharing with a client.',
    tips: ['The filters narrow the answers too, so you can read one segment\'s words.', 'Searchable by default.'],
    related: ['word-cloud'],
  },
  'word-cloud': {
    bestFor: ['A quick picture of the language people used', 'Pairing with the Open-End Explorer'],
    needs: 'One open-ended question.',
    watchOut: 'Sizes count respondents, not meaning -- "not cheap" and "cheap" both grow "cheap".',
    tips: ['Leave out study-specific words that crowd the cloud.', 'Raise the minimum count to drop one-off words.'],
    related: ['open-end-explorer'],
  },
};
